import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { Camera, CheckCircle, Clock, Plus, Receipt, XCircle } from 'lucide-react';

const statusStyles = {
  pending: 'border border-amber-200 bg-amber-50 text-amber-700',
  approved: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  rejected: 'border border-red-200 bg-red-50 text-red-600',
};

function formatWhen(value) {
  if (!value) return '';
  try { return new Date(value).toLocaleString(); } catch { return value; }
}

function ExpenseHistory({ expense }) {
  const reviewHistory = expense.review_history || [];
  const resubmitHistory = expense.resubmit_history || [];
  if (!reviewHistory.length && !resubmitHistory.length && !expense.last_notification) return null;
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-3 text-xs text-zinc-600" data-testid={`expense-history-${expense.id}`}>
      <p className="mb-2 font-semibold text-zinc-900">History</p>
      <div className="space-y-2">
        {reviewHistory.map((item, index) => (
          <div key={`review-${index}`} data-testid={`expense-review-history-${expense.id}-${index}`}>
            <span className="font-medium capitalize">{item.status}</span> by {item.reviewed_by_name || 'Reviewer'} {formatWhen(item.created_at)}{item.note ? ` · ${item.note}` : ''}
          </div>
        ))}
        {resubmitHistory.map((item, index) => (
          <div key={`resubmit-${index}`} data-testid={`expense-resubmit-history-${expense.id}-${index}`}>
            <span className="font-medium">Resubmitted</span> by {item.resubmitted_by_name || 'Submitter'} {formatWhen(item.created_at)}
          </div>
        ))}
        {expense.last_notification && (
          <div data-testid={`expense-notification-history-${expense.id}`}>
            Notification: {expense.last_notification?.email?.status || 'created'}{expense.last_notification?.email?.to ? ` to ${expense.last_notification.email.to}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ExpensesReviewPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [activeTab, setActiveTab] = useState('review');
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [addingExpense, setAddingExpense] = useState(false);
  const [dbas, setDbas] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (user?.role === 'field_staff') setActiveTab('mine');
  }, [user?.role]);

  useEffect(() => {
    Promise.all([api.get('/dbas'), api.get('/jobs')])
      .then(([dbaResponse, jobResponse]) => {
        setDbas(dbaResponse.data || []);
        setJobs((jobResponse.data || []).slice(0, 100));
      })
      .catch(() => {
        setDbas([]);
        setJobs([]);
      });
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      if (activeTab === 'mine' && user?.id) params.submitted_by = user.id;
      const { data } = await api.get('/expenses', { params });
      setExpenses(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, user?.id]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const totals = useMemo(() => expenses.reduce((acc, exp) => acc + (Number(exp.amount) || 0), 0), [expenses]);

  const reviewExpense = async (expenseId, status) => {
    setBusyId(expenseId);
    setError('');
    try {
      await api.put(`/expenses/${expenseId}/review`, {
        status,
        review_note: notes[expenseId] || '',
      });
      await fetchExpenses();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId('');
    }
  };

  const addExpense = async (event) => {
    event.preventDefault();
    setAddingExpense(true);
    setError('');
    const form = new FormData(event.target);
    try {
      const payload = new FormData();
      payload.append('category', form.get('category'));
      payload.append('amount', form.get('amount'));
      payload.append('description', form.get('description') || '');
      payload.append('date', form.get('date') || '');
      payload.append('dba_id', form.get('dba_id') || '');
      payload.append('job_id', form.get('job_id') || '');
      const receipt = form.get('receipt');
      if (receipt && receipt.size > 0) payload.append('receipt', receipt);
      if (editingExpense?.id) {
        await api.put(`/expenses/${editingExpense.id}/resubmit`, payload);
      } else {
        await api.post('/expenses/with-receipt', payload);
      }
      setShowAddExpense(false);
      setEditingExpense(null);
      event.target.reset();
      await fetchExpenses();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setAddingExpense(false);
    }
  };

  const openAddExpense = () => {
    setEditingExpense(null);
    setShowAddExpense(true);
  };

  const openResubmitExpense = (expense) => {
    setEditingExpense(expense);
    setShowAddExpense(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 border-b border-zinc-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="authority-overline">Expense control</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-950 font-heading" data-testid="expenses-review-title">Expenses</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="expenses-review-summary">{expenses.length} submissions · ${totals.toFixed(2)}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="expense-page-actions">
          <Button type="button" onClick={openAddExpense} data-testid="add-expense-button"><Plus className="mr-1 h-4 w-4" /> Add Expense</Button>
          <div className="flex flex-wrap gap-2" data-testid="expense-status-filters">
          {['pending', 'approved', 'rejected', 'all'].map(status => (
            <Button
              key={status}
              type="button"
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              data-testid={`expense-filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="expenses-tabs">
        <TabsList className="flex h-auto w-fit flex-wrap" data-testid="expenses-tabs-list">
          {user?.role !== 'field_staff' && <TabsTrigger value="review" data-testid="expenses-tab-review">Review Queue</TabsTrigger>}
          <TabsTrigger value="mine" data-testid="expenses-tab-mine">My Expenses</TabsTrigger>
        </TabsList>

        {error && <div className="mt-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="expenses-review-error">{error}</div>}

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="space-y-3" data-testid="expenses-review-loading">
              {[1, 2, 3].map(item => <div key={item} className="h-32 rounded-sm bg-zinc-200 animate-pulse" />)}
            </div>
          ) : expenses.length === 0 ? (
            <Card className="border border-zinc-200" data-testid="expenses-review-empty">
              <CardContent className="py-12 text-center">
                <Receipt className="mx-auto mb-2 h-8 w-8 text-zinc-400" />
                <p className="text-sm text-zinc-500">No expenses in this queue.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3" data-testid={activeTab === 'mine' ? 'my-expenses-list' : 'expenses-review-list'}>
              {expenses.map(expense => (
                <Card key={expense.id} className="border border-zinc-200 bg-white" data-testid={`expense-review-card-${expense.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base font-bold font-heading">
                      <Receipt className="h-4 w-4" />
                      <span className="capitalize" data-testid={`expense-review-category-${expense.id}`}>{expense.category}</span>
                      <span data-testid={`expense-review-amount-${expense.id}`}>${Number(expense.amount || 0).toFixed(2)}</span>
                    </CardTitle>
                    <p className="mt-1 text-xs text-zinc-500" data-testid={`expense-review-submitter-${expense.id}`}>{expense.submitted_by_name || 'Unknown'} · {expense.date || expense.created_at?.slice(0, 10)}</p>
                  </div>
                  <Badge variant="secondary" className={`w-fit text-xs ${statusStyles[expense.status] || 'bg-zinc-100 text-zinc-600'}`} data-testid={`expense-review-status-${expense.id}`}>
                    {expense.status === 'pending' && <Clock className="mr-1 h-3 w-3" />}
                    {expense.status === 'approved' && <CheckCircle className="mr-1 h-3 w-3" />}
                    {expense.status === 'rejected' && <XCircle className="mr-1 h-3 w-3" />}
                    {expense.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-zinc-700" data-testid={`expense-review-description-${expense.id}`}>{expense.description || 'No description'}</p>
                <div className="flex flex-wrap gap-2">
                  {expense.receipt_photo_id ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/photos/file/${expense.receipt_photo_id}`} target="_blank" rel="noopener noreferrer" data-testid={`expense-review-receipt-link-${expense.id}`}>
                      <Button type="button" variant="outline" size="sm" data-testid={`expense-review-receipt-button-${expense.id}`}><Camera className="mr-1 h-3.5 w-3.5" /> Open receipt</Button>
                    </a>
                  ) : (
                    <Badge variant="secondary" className="border border-zinc-200 bg-white text-zinc-500" data-testid={`expense-review-no-receipt-${expense.id}`}>No receipt</Badge>
                  )}
                </div>
                <ExpenseHistory expense={expense} />
                {activeTab === 'review' && user?.role === 'admin' && (
                  <>
                    <Textarea
                      value={notes[expense.id] ?? expense.review_note ?? ''}
                      onChange={(event) => setNotes(prev => ({ ...prev, [expense.id]: event.target.value }))}
                      placeholder="Add approval or rejection notes..."
                      rows={2}
                      data-testid={`expense-review-note-${expense.id}`}
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button type="button" variant="outline" disabled={busyId === expense.id} onClick={() => reviewExpense(expense.id, 'rejected')} data-testid={`expense-review-reject-${expense.id}`}><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
                      <Button type="button" disabled={busyId === expense.id} onClick={() => reviewExpense(expense.id, 'approved')} data-testid={`expense-review-approve-${expense.id}`}><CheckCircle className="mr-1 h-4 w-4" /> Approve</Button>
                    </div>
                  </>
                )}
                {activeTab === 'mine' && expense.status === 'rejected' && (
                  <div className="flex justify-end" data-testid={`expense-resubmit-actions-${expense.id}`}>
                    <Button type="button" variant="outline" onClick={() => openResubmitExpense(expense)} data-testid={`expense-resubmit-button-${expense.id}`}>Edit & Resubmit</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editingExpense ? 'Edit & Resubmit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
          <form onSubmit={addExpense} className="space-y-4" data-testid="add-expense-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Category</Label><select name="category" required defaultValue={editingExpense?.category || 'fuel'} className="h-9 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm" data-testid="expense-category-input"><option value="fuel">Fuel</option><option value="materials">Materials</option><option value="equipment">Equipment</option><option value="tolls">Tolls</option><option value="meals">Meals</option><option value="misc">Misc</option></select></div>
              <div><Label>Amount</Label><Input name="amount" type="number" min="0.01" step="0.01" required defaultValue={editingExpense?.amount || ''} data-testid="expense-amount-input" /></div>
              <div><Label>Date</Label><Input name="date" type="date" defaultValue={editingExpense?.date || ''} data-testid="expense-date-input" /></div>
              <div><Label>Business</Label><select name="dba_id" defaultValue={editingExpense?.dba_id || ''} className="h-9 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm" data-testid="expense-dba-input"><option value="">No business selected</option>{dbas.map((dba) => <option key={dba.id} value={dba.id}>{dba.name}</option>)}</select></div>
            </div>
            <div><Label>Job</Label><select name="job_id" defaultValue={editingExpense?.job_id || ''} className="h-9 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm" data-testid="expense-job-input"><option value="">No job selected</option>{jobs.map((job) => <option key={job.id} value={job.id}>{job.title || job.address || job.id}</option>)}</select></div>
            <div><Label>Receipt</Label><Input name="receipt" type="file" accept="image/png,image/jpeg,image/webp,image/gif" capture="environment" data-testid="expense-receipt-input" />{editingExpense?.receipt_photo_id && <p className="mt-1 text-xs text-zinc-500" data-testid="expense-existing-receipt-note">Existing receipt stays attached unless you upload a replacement.</p>}</div>
            <div><Label>Description</Label><Textarea name="description" rows={3} defaultValue={editingExpense?.description || ''} placeholder="What was this expense for?" data-testid="expense-description-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddExpense(false); setEditingExpense(null); }} data-testid="expense-cancel-button">Cancel</Button>
              <Button type="submit" disabled={addingExpense} data-testid="expense-submit-button">{editingExpense ? 'Resubmit Expense' : 'Submit Expense'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}